// src/pages/expense/business/businessDraftConfig.js
// ✅ 전 사업 확산 [3] — 경영팀 사업의 businessConfig. 공통 컴포넌트
// (pages/expense/_shared/ExpenseDraft*.jsx)에 이 config를 넘긴다.
// 템플릿 30종은 기존 DocumentTypeSelector.jsx의 "expense_draft-경영팀" 목록을
// 그대로 재사용(이미 실사용 검증된 세트라 새로 추리지 않음 — 소독과 달리 경영팀은
// 처음부터 풍부한 템플릿이 있었음). 기본값은 BusinessExpense 실측 179건 근거로
// 빈도가 뚜렷한 항목만 채우고, 나머지는 빈 값(자유 입력)으로 둔다.
import { makeExpenseDraftConfig } from "../_shared/expenseDraftConfig";
import VendorPanel from "../_shared/VendorPanel";
import ExpenseItemPanel from "../_shared/ExpenseItemPanel";
import SalaryPanel from "./SalaryPanel";

// ── 30종 + 기타 그리드 — DocumentTypeSelector.jsx의 "expense_draft-경영팀"과 동일 순서 ──
const TEMPLATES = [
  { code: "meeting_expense", label: "간담회비" },
  { code: "service_contract", label: "계약인지세" },
  { code: "deduction_refund", label: "공제금환급" },
  { code: "meal_allowance", label: "급량비" },
  { code: "donation", label: "기부금" },
  { code: "accounting_fee", label: "기장수수료" },
  { code: "general_purchase", label: "물품구매" },
  { code: "banner_ad", label: "배너광고" },
  { code: "corporate_seal", label: "법인인감증명서" },
  { code: "fund_transfer", label: "보전일시자금" },
  { code: "welfare_points", label: "복지포인트" },
  { code: "printer_rental", label: "복합기렌탈료" },
  { code: "office_maintenance", label: "사무실관리비" },
  { code: "biz_license", label: "사업자등록증" },
  { code: "server_fee", label: "서버이용료" },
  { code: "supplies_office", label: "소모(관서업무비)" },
  { code: "supplies", label: "소모품구매" },
  { code: "credit_card", label: "신용카드" },
  { code: "biz_report", label: "영업신고수수료" },
  { code: "postage", label: "우편발송" },
  { code: "withholding_tax", label: "원천세" },
  { code: "fuel_cost", label: "유류비(세차비)" },
  { code: "utility_fee", label: "인터넷·전화요금" },
  { code: "payroll", label: "임직원급여" },
  { code: "electric_bill", label: "전기료" },
  { code: "water_rental", label: "정수기렌탈료" },
  { code: "parking_fee", label: "주차비용" },
  { code: "job_training", label: "직무교육비" },
  { code: "car_installment", label: "차량할부금" },
  { code: "interview_fee", label: "채용심사비" },
  { code: "pension", label: "퇴직연금" },
];

// ✅ 종류 선택 시 자동 채워지는 구분/내용/분야. 2026-07-30 방침: "사용내역 구분"
// 기본값은 해당 템플릿 예산과목(expenseTemplates.js budgetItem) 마지막 항목과
// 일치시킨다(대조표 조사 결과 27/30 불일치 확인). 예외:
// - payroll/env_total_payroll/env_disinfection_payroll(급여↔보수),
//   utility_fee(KT전화요금), supplies_office(부서운영업무비) — 회계 확인 후
//   별도 지시 예정이라 보류, 기존 값 유지.
// - service_contract/deduction_refund/fund_transfer/credit_card/withholding_tax —
//   예산과목 필드 자체가 없어 비교 불가, 빈 값(자유 입력) 유지.
// item_name/field_name은 이번 변경 대상이 아니므로 기존 값 그대로 둔다(비어있던
// 항목은 계속 비워둠 — category_name만 새로 채움).
const CATEGORY_ITEM_FIELD_DEFAULTS = {
  meeting_expense: { category_name: "사업업무추진비", item_name: "업무추진 간담회비", field_name: "지출" },
  welfare_points: { category_name: "복지포인트", item_name: "복지포인트", field_name: "지급" },
  supplies: { category_name: "소모품비", item_name: "", field_name: "구입" },
  accounting_fee: { category_name: "세무회계 관련 수수료", item_name: "기장수수료", field_name: "지출" },
  corporate_seal: { category_name: "일반수용비", item_name: "법인인감증명서 발급", field_name: "지출" },
  payroll: { category_name: "급여", item_name: "", field_name: "지급" }, // ⏸ 보류(급여↔보수, 회계 확인 예정)
  fuel_cost: { category_name: "차량선박비", item_name: "유류비", field_name: "지출" },
  pension: { category_name: "퇴직연금", item_name: "퇴직연금", field_name: "납부" },
  water_rental: { category_name: "정수기 렌탈료", item_name: "정수기 렌탈료", field_name: "납부" },
  server_fee: { category_name: "일반수용비", item_name: "홈페이지 서버이용료", field_name: "납부" },
  utility_fee: { category_name: "공공운영비", item_name: "인터넷 및 전화 요금", field_name: "납부" }, // ⏸ 보류(KT전화요금, 회계 확인 예정)
  printer_rental: { category_name: "복합기 렌탈료", item_name: "사무실 복합기 렌탈료", field_name: "지출" },
  electric_bill: { category_name: "전기료", item_name: "전기요금(전기료)", field_name: "납부" },
  supplies_office: { category_name: "소모품", item_name: "", field_name: "구입" }, // ⏸ 보류(부서운영업무비, 회계 확인 예정)
  car_installment: { category_name: "자산취득비", item_name: "업무용 특장 차량 할부금", field_name: "지출" }, // 이미 일치
  // ✅ 신규 채움(예산과목 마지막 항목 기준) — 실측 데이터가 뚜렷하지 않아 비워뒀던
  // 항목 중 예산과목 필드가 있는 것만.
  meal_allowance: { category_name: "급량비", item_name: "", field_name: "" },
  donation: { category_name: "기부금", item_name: "", field_name: "" },
  general_purchase: { category_name: "사무용품비", item_name: "", field_name: "" },
  banner_ad: { category_name: "온라인 사업홍보", item_name: "", field_name: "" },
  office_maintenance: { category_name: "사무실 관리비", item_name: "", field_name: "" },
  biz_license: { category_name: "일반수용비", item_name: "", field_name: "" },
  biz_report: { category_name: "일반수용비", item_name: "", field_name: "" },
  postage: { category_name: "우편물발송 및 문자발송료", item_name: "", field_name: "" },
  parking_fee: { category_name: "일반수용비", item_name: "", field_name: "" },
  job_training: { category_name: "교육훈련비", item_name: "", field_name: "" },
  interview_fee: { category_name: "심사수당", item_name: "", field_name: "" },
  // 나머지(공제금환급/보전일시자금/신용카드/원천세/계약인지세)는 예산과목 필드가
  // 없어 비교 불가 — 빈 값(자유 입력) 유지.
};

// ✅ 구매/계약성 5종 + 인터넷·전화요금 — 품목 표(ExpenseItemPanel)로 이용내역
// 입력+자동 합산(다른 사업과 동일 관례). 임직원급여만 인건비 명단(SalaryPanel,
// 세금 자동계산 지원)을 쓴다.
const ITEM_PANEL_TEMPLATES = ["general_purchase", "supplies", "supplies_office", "service_contract", "fuel_cost", "utility_fee"];
const SALARY_PANEL_TEMPLATES = ["payroll"];

const businessDraftConfig = makeExpenseDraftConfig({
  namespace: "business-draft",
  category: "경영팀",
  businessLabel: "경영팀",
  // ✅ (구) 페이지가 실제로 쓰는 BusinessExpense.draft_no 접두어와 정확히 일치(DB
  // 실측: 기존 179건 전부 "금천일자리(주)경영지출").
  draftNoPrefix: "금천일자리(주)경영지출",
  dataApiBase: "/expense/business/",
  combinedCreateApi: "/expense/business/draft/",
  newRoutePath: "/expense/business/draft/new",
  listRoutePath: "/expense/business/draft/list",
  previewRoutePath: "/expense/business/draft/preview",
  templates: TEMPLATES,
  categoryItemFieldDefaults: CATEGORY_ITEM_FIELD_DEFAULTS,
  itemPanelTemplates: ITEM_PANEL_TEMPLATES,
  salaryPanelTemplates: SALARY_PANEL_TEMPLATES,
  // ✅ BusinessExpenseSerializer는 category_name/item_name/field_name이 아니라
  // service_type/service_content/service_location으로 노출 — env_total/env_disinfection과
  // 유일하게 다른 지점.
  fieldNameMap: { categoryName: "service_type", itemName: "service_content", fieldName: "service_location" },
  VendorPanel,
  ExpenseItemPanel,
  SalaryPanel,
});

export default businessDraftConfig;
